<script setup lang="ts">
import { computed, inject } from 'vue';
import {
  FormFieldGroupEmits,
  FormFieldGroupProps,
  FormFieldGroupRef,
} from '@/types/form/form-field-group';
import { DynamicFormField } from '@/types/form/dynamic-form';
import utils from '@/utils';
import { ScItem } from '@/types/sitecore';
import useDevice from '@/hooks/useDevice';

const props = defineProps<FormFieldGroupProps>();
const emit = defineEmits<FormFieldGroupEmits>();

const { largeThanTablet } = useDevice();

const fields = computed(() => props.item?.fields);
const items = computed(() => fields.value?.items ?? []);
const hasItems = computed(() => items.value.length > 0);

const desktopColumnLayout = computed(() => {
  let val = utils.sp.getVal(fields.value?.desktopColumnLayout);
  if (!val) val = '2';
  const num = Number(val);
  if (isNaN(num)) return val;
  return `repeat(${num}, 1fr)`;
});
const rootStyle = computed(() => {
  if (largeThanTablet && desktopColumnLayout.value) {
    return {
      '--column-layout': desktopColumnLayout.value,
    };
  }
  return {};
});

const getComponentType = (item: ScItem<DynamicFormField>) => {
  const fieldType = utils.sp.getVal(item?.fields?.fieldType);
  return fieldType ?? 'FormInput';
};

const onIconClick = (iconKey: string, value: string) => {
  emit('iconClick', iconKey, value);
};

const onChange = (fieldType: string, fieldName: string, value: any, originValue?: any) => {
  emit('change', fieldType, fieldName, value, originValue);
};
</script>
<template>
  <div class="f-field-group" :style="rootStyle" v-if="hasItems">
    <div class="f-field-group__items">
      <template v-for="(item, index) in items" :key="`${item.id}-${index}`">
        <component
          :is="getComponentType(item)"
          :item="item"
          @iconClick="onIconClick"
          @change="onChange"
          class="f-field-group__item"
        />
      </template>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

.f-field-group {
  width: 100%;

  &__items {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    @include tablet-landscape {
      grid-template-columns: var(--column-layout, repeat(2, 1fr));
      column-gap: 16px;
      row-gap: 16px;
    }
  }

  &__item {
    min-width: 0;
  }
}
</style>
